// Measures whether a human actually *read* an article (not just opened it).
//
// Emits two first-party events through the customer-intelligence tracker:
//   - article_open : once per article page view
//   - article_read : once per article page view, when the visitor crosses
//                    both an active-time and a scroll-depth threshold
//
// Active time only accrues while the tab is visible and the visitor has
// interacted in the last 30s, so background tabs never count as reads.
import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";
import { track } from "@/lib/customer-intelligence/tracker";

const READ_ACTIVE_MS = 30_000;
const READ_SCROLL_PCT = 50;
const IDLE_MS = 30_000;

function articleSlug(pathname: string): string | null {
  if (!pathname.startsWith("/blog/")) return null;
  const slug = pathname.slice("/blog/".length).replace(/\/+$/, "");
  if (!slug || slug.includes("/")) return null;
  return slug.slice(0, 160);
}

export function ArticleReadTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const slug = articleSlug(pathname);
    if (!slug) return;

    let activeMs = 0;
    let maxScroll = 0;
    let lastTick = Date.now();
    let lastActivity = Date.now();
    let readFired = false;

    track("article_open", { slug, path: pathname });

    const bump = () => {
      lastActivity = Date.now();
    };
    const onScroll = () => {
      bump();
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) {
        maxScroll = 100;
        return;
      }
      const pct = Math.min(100, Math.round((window.scrollY / total) * 100));
      if (pct > maxScroll) maxScroll = pct;
    };

    const tick = () => {
      const now = Date.now();
      const dt = now - lastTick;
      lastTick = now;
      if (!document.hidden && now - lastActivity < IDLE_MS) activeMs += dt;
      if (!readFired && activeMs >= READ_ACTIVE_MS && maxScroll >= READ_SCROLL_PCT) {
        readFired = true;
        track("article_read", {
          slug,
          path: pathname,
          activeTimeMs: Math.round(activeMs),
          maxScrollPct: maxScroll,
        });
      }
    };

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "keydown",
      "click",
      "touchstart",
      "wheel",
    ];
    for (const e of events) window.addEventListener(e, bump, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    const timer = window.setInterval(tick, 1000);

    return () => {
      window.clearInterval(timer);
      for (const e of events) window.removeEventListener(e, bump);
      window.removeEventListener("scroll", onScroll);
      tick();
    };
  }, [pathname]);

  return null;
}
